import type { ProjectedRecord } from "@storyboard/driver-query/predicate";
import { isAnchorAcknowledgementSubject } from "@/models/acknowledgement/acknowledgementStore";
import type { DriverRow } from "@/types/driver";
import { type AssignmentFold, assignmentTargetKey, type OpenAssignment } from "./assignments";
import type { DispatchItem } from "./dispatchItems";

/**
 * One Dispatch table row at either grain: a driver, or one message on a driver's channel. Both carry the driver and a
 * flattened record, projected once so filters, sort, and cells agree.
 *
 * `id` is what the table, the highlight, and the expanded row key on: the membership id or the item id, except when
 * grouping lands one row under several groups and each copy needs its own. A driver row carries `unacknowledgedCount`,
 * how many of the driver's messages wait on a dispatcher (zero when the channel is handled), beside the driver's read
 * state; a message row's item carries both for the message.
 * The store calls a message an `Item`, so the message row holds a `DispatchItem`.
 */
export type DispatchTableRow =
  | { kind: "driver"; id: string; driver: DriverRow; unacknowledgedCount: number; record: ProjectedRecord }
  | { kind: "message"; id: string; item: DispatchItem; driver: DriverRow; record: ProjectedRecord };

/** Whether the row's Message describes the driver's photo rather than quoting the driver: the message, or the latest. */
export function rowMessageIsPhoto(row: DispatchTableRow): boolean {
  return row.kind === "message" ? row.item.photo : row.driver.recentSource === "photo";
}

/** Whether anyone has read what the row is about: the message, or the driver's channel. Painted as the row tint. */
export function rowUnread(row: DispatchTableRow): boolean {
  return row.kind === "message" ? row.item.unread : row.driver.unread === true;
}

/** Whether the row still waits on a dispatcher: the message, or any message on the channel. Painted as the red dot. */
export function rowUnacknowledged(row: DispatchTableRow): boolean {
  return row.kind === "message" ? !row.item.acknowledged : row.unacknowledgedCount > 0;
}

/**
 * The rows showing the red dot, one id per row: a driver row counts once however many of its messages wait.
 *
 * @param excluded - Row ids left out: those the reassignment prototype handed elsewhere, whose dot is purple.
 */
export function unacknowledgedRowIds(
  rows: readonly DispatchTableRow[],
  excluded: ReadonlySet<string> = new Set(),
): string[] {
  return rows.filter((row) => rowUnacknowledged(row) && !excluded.has(row.id)).map((row) => row.id);
}

/** The feed a reply to the row goes to and its History opens: the message's feed, or the driver's alias feed. */
export function rowFeedId(row: DispatchTableRow): string | null {
  return row.kind === "message" ? row.item.feedId : row.driver.feedId;
}

/**
 * Every open assignment on the row, newest first: a message row's own target (the message, or the TMS event's anchor,
 * as its acknowledgement names it), or on a driver row any open assignment on the driver's channel.
 */
export function rowAssignments(row: DispatchTableRow, fold: AssignmentFold): readonly OpenAssignment[] {
  if (row.kind === "driver") return row.driver.feedId ? (fold.byFeed.get(row.driver.feedId) ?? []) : [];
  const event = row.item.tmsEvent;
  const key =
    row.item.source === "tmsEvent" && event && isAnchorAcknowledgementSubject(event.subject)
      ? assignmentTargetKey("anchor", event.id)
      : assignmentTargetKey("feed_item", row.item.id);
  const open = fold.byTarget.get(key);
  return open ? [open] : [];
}

/** The newest open assignment on the row, or `null`: what its chip and dot show. */
export function rowAssignment(row: DispatchTableRow, fold: AssignmentFold): OpenAssignment | null {
  return rowAssignments(row, fold)[0] ?? null;
}
